import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { projectAPI, paymentAPI, exportAPI, downloadBlob } from '../api';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Download } from 'lucide-react';

const money = (v) => (v == null ? '\u2014' : `\u20b9${Number(v).toLocaleString('en-IN')}`);

const SUMMARY = [
  { key: 'total', label: 'Total Projects', color: 'text-slate-700 dark:text-slate-200' },
  { key: 'ongoing', label: 'Ongoing', color: 'text-blue-600' },
  { key: 'completed', label: 'Completed', color: 'text-green-600' },
  { key: 'delayed', label: 'Delayed', color: 'text-red-600' },
  { key: 'waiting_approval', label: 'Waiting Approval', color: 'text-amber-600' },
  { key: 'payment_pending', label: 'Payment Pending', color: 'text-orange-600' },
];

export const AllProjectsPage = () => {
  const { hasPermission } = useAuth();
  const [data, setData] = useState({ summary: {}, projects: [] });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ service: '', status: '', city: '', client_type: '', architect_id: '' });
  const [drill, setDrill] = useState('');
  const [outstanding, setOutstanding] = useState(null);
  const [reminders, setReminders] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {}; Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await projectAPI.list(params);
      setData(res.data);
    } catch (e) { toast.error('Failed to load projects'); }
    finally { setLoading(false); }
  }, [filters]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (hasPermission && hasPermission('payment.view')) paymentAPI.reminders().then(r => setReminders(r.data)).catch(() => {}); }, [hasPermission]);

  const showOutstanding = async () => {
    try { const res = await paymentAPI.summary(); setOutstanding(res.data); }
    catch (e) { toast.error('Not permitted to view payments'); }
  };

  const opts = useMemo(() => {
    const uniq = (fn) => Array.from(new Set(data.projects.map(fn).filter(Boolean))).sort();
    return { service: uniq(p => p.service), status: uniq(p => p.status), city: uniq(p => p.city) };
  }, [data.projects]);

  const rows = useMemo(() => {
    if (!drill) return data.projects;
    return data.projects.filter(p => {
      if (drill === 'completed') return ['completed', 'delivered'].includes(p.status) || p.progress === 100;
      if (drill === 'delayed') return p.delayed;
      if (drill === 'payment_pending') return (p.outstanding || 0) > 0;
      if (drill === 'waiting_approval') return p.current_stage && /approval|review/i.test(p.current_stage);
      if (drill === 'ongoing') return !p.delayed && !['completed', 'delivered'].includes(p.status);
      return true;
    });
  }, [drill, data.projects]);

  if (loading) return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  const s = data.summary || {};

  return (
    <div className="space-y-6" data-testid="all-projects-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-outfit font-semibold">All Projects</h1>
          <p className="text-muted-foreground mt-1">Central view of all converted/assigned projects.</p>
        </div>
        {hasPermission && hasPermission('report.export') && (
          <button onClick={async () => { try { const res = await exportAPI.download('projects', { fmt: 'xlsx' }); downloadBlob(res, 'projects.xlsx'); toast.success('Export ready'); } catch (e) { toast.error('Export failed'); } }}
            data-testid="projects-export" className="inline-flex items-center gap-1 px-3 py-2 text-sm bg-primary/10 text-primary rounded-lg"><Download className="h-4 w-4" /> Export</button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {SUMMARY.map(c => (
          <button key={c.key} onClick={() => setDrill(drill === c.key ? '' : c.key)} data-testid={`proj-summary-${c.key}`}
            className={`clay-card p-4 text-center transition ${drill === c.key ? 'ring-2 ring-primary' : ''}`}>
            <div className={`text-2xl font-semibold ${c.color}`}>{s[c.key] ?? 0}</div>
            <div className="text-xs text-muted-foreground mt-1">{c.label}</div>
          </button>
        ))}
      </div>

      {reminders && (reminders.counts.overdue > 0 || reminders.counts.due_today > 0) && (
        <div className="clay-card p-4 border-l-4 border-orange-500" data-testid="payment-reminders">
          <h3 className="font-medium mb-1">Payment Reminders</h3>
          <p className="text-sm text-muted-foreground mb-2">{reminders.counts.overdue} overdue · {reminders.counts.due_today} due today</p>
          <ul className="space-y-1 text-sm">
            {[...reminders.overdue.map(r => ({ ...r, kind: 'overdue' })), ...reminders.due_today.map(r => ({ ...r, kind: 'due' }))].slice(0, 8).map(r => (
              <li key={r.lead_id} className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-1">
                <Link to={`/leads/${r.lead_id}`} className="text-primary hover:underline">{r.project_serial || r.client}</Link>
                <span className={r.kind === 'overdue' ? 'text-red-600' : 'text-orange-600'}>{money(r.amount_due)} · {r.kind === 'overdue' ? `${r.days_overdue}d overdue` : 'due today'}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {hasPermission && hasPermission('payment.view') && (
        <div className="clay-card p-4 flex items-center justify-between" data-testid="proj-outstanding">
          <div>
            <div className="text-sm text-muted-foreground">Total Outstanding</div>
            <div className="text-2xl font-semibold text-orange-600">{money(s.total_outstanding)}</div>
          </div>
          <button onClick={showOutstanding} className="px-4 py-2 bg-primary text-white rounded-lg text-sm">View Breakdown</button>
        </div>
      )}

      {outstanding && (
        <div className="clay-card p-4" data-testid="proj-outstanding-breakdown">
          <h3 className="font-medium mb-2">Outstanding by Project ({outstanding.pending_count})</h3>
          <div className="overflow-x-auto"><table className="w-full text-sm"><thead className="text-left text-muted-foreground"><tr><th className="py-2">Serial</th><th>Client</th><th>Deal</th><th>Received</th><th>Outstanding</th></tr></thead>
            <tbody>{outstanding.outstanding_breakdown.map(b => (
              <tr key={b.lead_id} className="border-t border-slate-100 dark:border-slate-800"><td className="py-2"><Link to={`/leads/${b.lead_id}`} className="text-primary hover:underline">{b.project_serial || '\u2014'}</Link></td><td>{b.client}</td><td>{money(b.total_deal_amount)}</td><td>{money(b.total_received)}</td><td className="text-orange-600">{money(b.outstanding)}</td></tr>
            ))}</tbody></table></div>
        </div>
      )}

      <div className="clay-card p-4 grid grid-cols-2 md:grid-cols-5 gap-3">
        {[['service', 'Service'], ['status', 'Status'], ['city', 'City']].map(([n, l]) => (
          <div key={n}><label className="block text-xs font-medium mb-1">{l}</label>
            <select value={filters[n]} onChange={e => setFilters({ ...filters, [n]: e.target.value })} data-testid={`proj-filter-${n}`}
              className="w-full px-2 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm">
              <option value="">All</option>{(opts[n] || []).map(o => <option key={o} value={o}>{o}</option>)}
            </select></div>
        ))}
        <div><label className="block text-xs font-medium mb-1">Type</label>
          <select value={filters.client_type} onChange={e => setFilters({ ...filters, client_type: e.target.value })} data-testid="proj-filter-type"
            className="w-full px-2 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm">
            <option value="">All</option><option value="b2c">B2C</option><option value="b2b">B2B</option>
          </select></div>
      </div>

      <div className="clay-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800/50 text-left"><tr>
            {['Serial', 'Client', 'Service', 'Value', 'Received', 'Outstanding', 'Stage', 'Sales Exec', 'Architect', 'Due', 'Progress'].map(h => <th key={h} className="px-3 py-3 font-medium whitespace-nowrap">{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={11} className="px-4 py-8 text-center text-muted-foreground">No projects.</td></tr> :
              rows.map(p => (
                <tr key={p.lead_id} className="border-t border-slate-100 dark:border-slate-800" data-testid="proj-row">
                  <td className="px-3 py-2"><Link to={`/leads/${p.lead_id}`} className="text-primary hover:underline">{p.project_serial || '\u2014'}</Link></td>
                  <td className="px-3 py-2">{p.client} {p.client_type === 'b2b' && <span className="text-xs px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 ml-1">B2B</span>}</td>
                  <td className="px-3 py-2">{p.service || '\u2014'}</td>
                  <td className="px-3 py-2">{money(p.project_value)}</td>
                  <td className="px-3 py-2">{money(p.received)}</td>
                  <td className={`px-3 py-2 ${p.outstanding > 0 ? 'text-orange-600' : ''}`}>{money(p.outstanding)}</td>
                  <td className="px-3 py-2">{p.current_stage || '\u2014'}</td>
                  <td className="px-3 py-2">{p.sales_executive || '\u2014'}</td>
                  <td className="px-3 py-2">{p.architect || '\u2014'}</td>
                  <td className={`px-3 py-2 whitespace-nowrap ${p.delayed ? 'text-red-600 font-medium' : ''}`}>{p.due_date ? new Date(p.due_date).toLocaleDateString('en-IN') : '\u2014'}</td>
                  <td className="px-3 py-2">{p.progress || 0}%</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
export default AllProjectsPage;
